import React, { useRef, useState, useEffect, useCallback } from "react";
import { Table as AntTable, DatePicker } from "antd";
import CommonSelect from "../../../core/common/commonSelect";
import Table from "../../../core/common/dataTable/index";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { useAttendance } from "./hooks/useAttendance";
import { StudentAttendanceTypes } from "./types/attendanceTypes";
import { useCourse } from "../../configuration/course/hooks/useCourse";
import { useBatchTiming } from "../../configuration/batch-timing/hooks/useBatchTiming";
import { useCenter } from "../../peoples/centers/hooks/useCenter";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../core/data/slice/authSlice";
import dayjs from "dayjs";

const StudentNameCell = React.memo(({ record, routes }: { record: StudentAttendanceTypes, routes: any }) => (
  <div className="d-flex align-items-center">
    <Link to="#" className="avatar avatar-md">
      {record.img.includes("get-thumbnail") ? (
        <img
          src={record.img}
          className="img-fluid rounded-circle"
          alt={record.name}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            const target = e.target as HTMLImageElement;
            target.src =
              record.gender?.toLowerCase() === "female"
                ? "/assets/img/profiles/default-female.png"
                : "/assets/img/profiles/default-male.png";
          }}
        />
      ) : (
        <ImageWithBasePath
          src={
            record.gender?.toLowerCase() === "female"
              ? "assets/img/profiles/default-female.png"
              : "assets/img/profiles/default-male.png"
          }
          className="img-fluid rounded-circle"
          alt={record.name}
        />
      )}
    </Link>
    <div className="ms-2">
      <Link
        to={routes.studentAttendanceDetail.replace(
          ":id",
          record.student.id.toString()
        )}
        className="link-primary"
      >
        {record.name}
      </Link>
    </div>
  </div>
));

const AttendanceStatusCell = React.memo(({
  record,
  filterDate,
  currentDate,
  statusColor,
  handleOptionChange,
  isSuperAdmin
}: any) => {
  const isPastDate = filterDate < currentDate;
  const isStatusAvailable = !!record.attendanceStatus;
  const regDate = record.registrationDate ? record.registrationDate.split("T")[0] : null;
  const isBeforeJoinDate = !!(regDate && filterDate < regDate);

  if (isBeforeJoinDate) {
    return (
      <span
        className="badge bg-light text-muted d-inline-flex align-items-center py-2 px-2"
        title={`Student joined on ${dayjs(record.registrationDate).format("DD-MM-YYYY")}. Cannot mark attendance before date of join.`}
      >
        <i className="ti ti-calendar-off fs-5 me-1 text-danger"></i>
        Joined {dayjs(record.registrationDate).format("DD-MM-YYYY")}
      </span>
    );
  }

  if (isSuperAdmin) {
    const isMarked = !!record.attendanceStatus;
    const statusText = isMarked ? record.attendanceStatus.charAt(0).toUpperCase() + record.attendanceStatus.slice(1) : "Not marked yet";
    return (
      <span className={`badge ${statusColor || "bg-light text-muted"} d-inline-flex align-items-center`}>
        {isMarked && <i className="ti ti-circle-filled fs-5 me-1"></i>}
        {statusText}
      </span>
    );
  }

  return (
    <select
      className={`form-select ${statusColor}`}
      value={record.attendanceStatus.toLowerCase()}
      onChange={(e) => handleOptionChange(record.key, e.target.value)}
      disabled={isPastDate && isStatusAvailable}
      style={
        isPastDate && isStatusAvailable
          ? {
            cursor: "not-allowed",
            appearance: "none",
            WebkitAppearance: "none",
            MozAppearance: "none",
            backgroundImage: "none",
          }
          : {}
      }
    >
      <option className="text-muted" value="">
        Select Status
      </option>
      <option className="text-muted" value="present">
        Present
      </option>
      <option className="text-muted" value="absent">
        Absent
      </option>
      <option className="text-muted" value="leave">
        Leave
      </option>
      <option className="text-muted" value="ncns">
        NCNS
      </option>
      <option className="text-muted" value="dropout">
        Dropout
      </option>
    </select>
  );
});

// Main student attendance management component
const StudentAttendance = () => {
  const routes = all_routes;
  const currentDate = new Date().toISOString().split("T")[0];
  const userRole = useSelector(selectUserRole);
  const isSuperAdmin = userRole === "superadmin";
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);

  const {
    students,
    isLoading,
    selectedStudentKeys,
    bulkStatus,
    filterDate,
    paginationData,
    setSelectedStudentKeys,
    setBulkStatus,
    setFilterDate,
    handleOptionChange,
    handleBulkStatusChange,
    isBulkUpdating,
    getStatusColor,
    handleFilterChange,
    handleApplyFilters,
    handleResetFilters,
    filters,
    tempFilters,
    setTempFilters,
    selectedCourseStages,
    pageNumber,
    recordsPerPage,
    search,
    handlePageChange,
    handleSearchChange,
    isAllMode,
    setIsAllMode,
    accumulatedStudents,
    lazyHasMore,
    isLazyLoading,
    loadNextLazyPage,
  } = useAttendance();

  const displayedStudents = isAllMode ? accumulatedStudents : students;

  const { courses } = useCourse({ size: 100, skip: !isFilterOpen }).getCourseState();
  const { batchTimings } = useBatchTiming({ size: 100, skip: !isFilterOpen });
  const { useCenterList } = useCenter({ skip: true });
  const { data: centerResponse } = useCenterList({ skip: userRole !== "superadmin" || !isFilterOpen });
  const centers = centerResponse?.data?.items || [];

  const courseOptions = courses.map((course) => ({
    value: course.id.toString(),
    label: course.name,
  }));

  const batchOptions = batchTimings.map((batch) => ({
    value: batch.id.toString(),
    label: `${batch.name} (${batch.start_time} - ${batch.end_time})`,
  }));

  const selectedCourse = courses.find((c) => c.id.toString() === filters.course_id?.toString());
  const derivedCourseStages = selectedCourse?.courseStages || selectedCourseStages || [];

  const stageOptions = derivedCourseStages.map((stage: any) => ({
    value: stage.id.toString(),
    label: stage.name,
  }));

  const centerOptions = centers.map((center: any) => ({
    value: center.id.toString(),
    label: center.name,
  }));

  const attendanceStatusOptions = [
    { value: "present", label: "Present" },
    { value: "absent", label: "Absent" },
    { value: "leave", label: "Leave" },
    { value: "ncns", label: "NCNS" },
    { value: "dropout", label: "Dropout" },
    { value: "not_marked", label: "Not Marked" },
  ];

  const findOptionByValue = (options: any[], value: string | undefined) => {
    if (!value) return undefined;
    return options.find((option) => option.value === value?.toString());
  };

  // Sync tempFilters when opening the filter dropdown
  const syncTempFilters = () => {
    setTempFilters(filters);
  };

  // Active filter count badge
  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== undefined && v !== null && v !== ""
  ).length;

  // Table column configuration
  const columns = React.useMemo(() => [
    {
      title: "Student ID",
      dataIndex: "studentId",
      render: (text: string, record: StudentAttendanceTypes) => (
        <Link
          to={routes.studentAttendanceDetail.replace(
            ":id",
            record.student.id.toString()
          )}
          className="link-primary"
        >
          {record.studentId}
        </Link>
      ),
      sorter: false,
    },
    {
      title: "Name",
      dataIndex: "name",
      render: (text: string, record: StudentAttendanceTypes) => (
        <StudentNameCell record={record} routes={routes} />
      ),
      sorter: false,
    },
    {
      title: "Course",
      dataIndex: "course",
      sorter: false,
    },
    {
      title: "Level",
      dataIndex: "level",
      sorter: false,
    },
    {
      title: "Week",
      dataIndex: "week",
      render: (_: any, record: StudentAttendanceTypes) => (
        <span>{record.week != null ? `Week ${record.week}` : "-"}</span>
      ),
      sorter: false,
    },
    ...(isSuperAdmin
      ? [
        {
          title: "Center",
          dataIndex: ["student", "center", "name"],
          render: (_: string, record: StudentAttendanceTypes) => (
            <span>{record.student?.center?.name || "-"}</span>
          ),
          sorter: false,
        },
      ]
      : []),
    {
      title: "Attendance Status",
      dataIndex: "attendanceStatus",
      render: (text: string, record: StudentAttendanceTypes) => (
        <AttendanceStatusCell
          record={record}
          filterDate={filterDate}
          currentDate={currentDate}
          statusColor={getStatusColor(record.attendanceStatus)}
          handleOptionChange={handleOptionChange}
          isSuperAdmin={isSuperAdmin}
        />
      ),
      sorter: false,
    },
  ], [isSuperAdmin, getStatusColor, filterDate, currentDate, handleOptionChange, routes.studentAttendanceDetail]);

  const getCheckboxProps = React.useCallback((record: StudentAttendanceTypes) => {
    const isPastLocked = !!record?.attendanceStatus && filterDate !== currentDate;
    const regDate = record?.registrationDate ? record.registrationDate.split("T")[0] : null;
    const isBeforeJoinDate = !!(regDate && filterDate < regDate);
    return {
      disabled: isPastLocked || isBeforeJoinDate,
    };
  }, [filterDate, currentDate]);

  const paginationConfig = React.useMemo(() => ({
    current: isAllMode ? 1 : pageNumber,
    pageSize: isAllMode ? (paginationData.totalItems || 1) : recordsPerPage,
    total: paginationData.totalItems || 0,
    onChange: handlePageChange || (() => { }),
    pageSizeOptions: ["All", 10, 20, 30],
  }), [isAllMode, pageNumber, paginationData.totalItems, recordsPerPage, handlePageChange]);



  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Student Attendance</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Student Attendance
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">
                Student Attendance List ({paginationData.totalItems || 0})
              </h4>
              <div className="d-flex align-items-center flex-wrap">
                {/* Date Picker */}
                <div className="mb-3 me-2 position-relative" style={{ display: "inline-block" }}>
                  <i
                    className="ti ti-calendar"
                    style={{
                      position: "absolute",
                      left: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "16px",
                      color: "#6b7280",
                      zIndex: 1,
                      pointerEvents: "none",
                    }}
                  />
                  <DatePicker
                    value={filterDate ? dayjs(filterDate) : null}
                    format="DD-MM-YYYY"
                    onChange={(date) => {
                      if (!date) return;
                      setFilterDate(date.format("YYYY-MM-DD"));
                      handlePageChange(1);
                    }}
                    disabledDate={(current) => {
                      if (current && current > dayjs().endOf("day")) return true;
                      return false;
                    }}
                    style={{ paddingLeft: "32px" }}
                    suffixIcon={null}
                  />
                </div>

                {/* Filter Dropdown */}
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                    onClick={() => {
                      setIsFilterOpen(true);
                      syncTempFilters();
                    }}
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                    {activeFilterCount > 0 && (
                      <span className="badge bg-primary ms-2">{activeFilterCount}</span>
                    )}
                  </Link>
                  <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 pb-0 border-bottom">
                        <div className="row">
                          {isSuperAdmin && (
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Center</label>
                                <CommonSelect
                                  className="select"
                                  options={centerOptions}
                                  value={findOptionByValue(centerOptions, tempFilters.center_id?.toString())}
                                  onChange={(value) => handleFilterChange("center_id", value)}
                                />
                              </div>
                            </div>
                          )}
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Course</label>
                              <CommonSelect
                                className="select"
                                options={courseOptions}
                                value={findOptionByValue(courseOptions, tempFilters.course_id?.toString())}
                                onChange={(value) => handleFilterChange("course_id", value, courses)}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Level</label>
                              <CommonSelect
                                className="select"
                                options={stageOptions}
                                value={findOptionByValue(stageOptions, tempFilters.stage_id?.toString())}
                                onChange={(value) => handleFilterChange("stage_id", value)}
                                isDisabled={!tempFilters.course_id}
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Attendance Status</label>
                              <CommonSelect
                                className="select"
                                options={attendanceStatusOptions}
                                value={findOptionByValue(attendanceStatusOptions, tempFilters.attendance_status)}
                                onChange={(value) => handleFilterChange("attendance_status", value)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <button
                          type="button"
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleApplyFilters}
                        >
                          Apply
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-body p-0 py-3">
              {/* Bulk Actions Bar - always visible for non-superadmin */}
              {!isSuperAdmin && (
                <div className="mx-3 mb-3">
                  <div className="bulk-actions-highlight bg-primary text-white rounded shadow-md p-3 d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="d-flex align-items-center">
                      <div className="bg-white text-primary rounded-circle d-flex align-items-center justify-content-center me-3 fw-bold fs-3 shadow-sm" style={{ width: "48px", height: "48px", minWidth: "48px" }}>
                        {selectedStudentKeys.length}
                      </div>
                      <div>
                        <h6 className="text-white mb-0 fw-bold">Students Selected</h6>
                        <p className="text-white-50 small mb-0">Select an action to apply to all selected students</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-grow-1 justify-content-md-end">
                      <select
                        className="form-select border-0 bg-white"
                        value={bulkStatus}
                        onChange={(e) => setBulkStatus(e.target.value)}
                        style={{ width: "180px", height: "42px" }}
                      >
                        <option>Set Attendance Status</option>
                        <option value="present">Present</option>
                        <option value="absent">Absent</option>
                        <option value="leave">Leave</option>
                        <option value="ncns">NCNS</option>
                        <option value="dropout">Dropout</option>
                      </select>
                      <button
                        className="btn btn-warning rounded px-4 fw-bold h-100 d-flex align-items-center"
                        onClick={handleBulkStatusChange}
                        disabled={selectedStudentKeys.length === 0 || isBulkUpdating}
                        style={{ height: "42px" }}
                      >
                        {isBulkUpdating ? (
                          <>
                            <i className="ti ti-loader animate-spin me-2"></i>
                            Applying...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-check me-2"></i>
                            Apply Status
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Table */}
              {isLoading && !isAllMode ? (
                <div className="text-center py-4">Loading attendance data...</div>
              ) : (
                <>
                  <Table
                    dataSource={displayedStudents}
                    columns={columns}
                    Selection={!isSuperAdmin}
                    onSelectionChange={setSelectedStudentKeys}
                    onSearchChange={handleSearchChange}
                    rowKey="key"
                    selectedKeys={selectedStudentKeys}
                    getCheckboxProps={getCheckboxProps}
                    pagination={paginationConfig}
                    summary={
                      isAllMode && isLazyLoading
                        ? () => (
                          <AntTable.Summary.Row>
                            <AntTable.Summary.Cell
                              index={0}
                              colSpan={columns.length + 1}
                            >
                              <div className="text-center py-3">Loading attendance data...</div>
                            </AntTable.Summary.Cell>
                          </AntTable.Summary.Row>
                        )
                        : undefined
                    }
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentAttendance;
